import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import { Button } from 'react-bootstrap5';

import routes from 'app/routes';
import ExternalLink from 'components/Widgets/ExternalLink';
import LoadingPlaceholder from 'components/new_ui/WaitLayers/LoadingPlaceholder';
import Alert from 'components/new_ui/Alerts/alert';

import { useContext } from '../Context';
import Tile from './Tile';

const priceByCurrency = (abbr) => {
  if (abbr === 'GBP' || abbr === 'CHF') {
    return 100;
  } if (abbr === 'USD') {
    return 100;
  }
  return 135;
};

export default function Template({ template, variant, policyEnabled }) {
  const [checkPossibleStart, setCheckPossibleStart] = useState(false);

  const includesPlatform = template.attributes.rules.some((item) => item.type === 'include_platform');
  const limits = template.attributes.rules.filter((rule) => ['price_minimal', 'employee_minimal'].includes(rule.type));

  const { state: statePlans, actions: actionsPlans } = useContext();

  const abbr = statePlans.currency;
  const price = template.attributes.price.find((item) => item.currency === abbr)?.price;
  const isFree = template.attributes.is_free;

  const navigate = useNavigate();

  let mark = '';

  switch (abbr) {
    case 'GBP':
      mark = '£';
      break;
    case 'CHF':
      mark = '₣';
      break;
    default:
      mark = '$';
  }

  const onStartClick = () => {
    setCheckPossibleStart(true);
    actionsPlans.isPossibleCreatePlan({
      policyTemplateId: template.id,
      successCallback: (result) => {
        setCheckPossibleStart(false);
        if (result.possible) {
          navigate(
            routes.managers.plans.new.company,
            {
              state: {
                data: { template, isFree },
              },
            },
          );
        } else {
          Alert({
            title: "Maximize Your Team's Potential!",
            text: `You're currently on our Free Platform Plan. 
                  To truly accelerate your team's development, 
                  consider upgrading to our Professional or Executive programs. 
                  Unlock advanced features and tailored solutions for your team's growth. 
                  Upgrade now and lead your team to new heights!`,
          });
        }
      },
    });
  };

  return (
    <Tile
      variant={variant}
      title={template.attributes.title}
      additivePolicy={includesPlatform}
    >
      <ul className="mt-1 mb-auto fw-light small">
        {template.attributes.items.map((item) => <li key={item.id}>{item.title}</li>)}
      </ul>

      <div className="mt-3 mb-3 text-center">
        {!template.attributes.contact_sales && (
          <>
            <div className="small fw-light">
              {`${template.attributes.title} starts from:`}
            </div>

            <div className="price fw-semibold">
              {isFree && <span className="fs-2">Free</span>}
              {!isFree > 0.0 && (
                <>
                  <span className="fs-2">
                    {`${price}`}
                  </span>
                  <span className="ms-1">
                    {abbr}
                  </span>
                </>
              )}
            </div>
            <div className="small fw-light">
              {`${isFree ? '' : '/'} ${template.attributes.price_scale}`}
            </div>
          </>
        )}

        <div className="mt-2 small fw-light text-gray">
          {isFree && <div>No Minimum</div>}
          {!isFree && limits.map((item) => (
            <div key={item.type}>
              {item.type === 'price_minimal' ? (
                `${mark}${priceByCurrency(abbr)}/Month Minimum`
              ) : (
                `${item.value} Employee Minimum`
              )}
            </div>
          ))}

          {limits.length === 0 && (
            <>
              No Employee Minimum
              <br />
            </>
          )}
        </div>
      </div>

      {(template.attributes.contact_sales || !policyEnabled || statePlans.enterprise.attributes.is_lock) ? (
        <ExternalLink
          href="https://meetings.hubspot.com/viktor-bullain"
          variant="outline-primary"
          className="w-100"
        >
          Contact Sales
        </ExternalLink>
      ) : (
        <Button
          variant={isFree ? 'primary' : 'outline-primary'}
          className="w-100"
          onClick={onStartClick}
        >
          {checkPossibleStart && (
            <LoadingPlaceholder placement="inline" text=" " />
          )}

          Start
        </Button>
      )}
    </Tile>
  );
}

Template.propTypes = {
  template: PropTypes.object.isRequired,
  variant: PropTypes.string,
  policyEnabled: PropTypes.bool,
};

Template.defaultProps = {
  variant: 'primary',
  policyEnabled: true,
};
